// Every lazy page and dialog in the app loads through here (React.lazy is
// banned by lint outside this file).
//
// A deploy replaces /app/static/ wholesale: the Cloud Run image carries only
// its own build's chunks and the GCS apex rsync deletes the old ones. A tab
// opened before the deploy still runs the old main.js, which names the old
// chunk hashes, so its next lazy import 404s and React.lazy rejects for good
// (mustang, 2026-10-12T20:41:45Z: /app/static/js/73824.81d64a95.chunk.js 404,
// the Hub never opened; the live main.js maps 73824 to f665f99c).
//
// A failed chunk is retried once (a dropped connection recovers there). If the
// retry fails too, the page reloads once to pick up the new index.html and its
// chunk names. A sessionStorage flag stops that reload from looping: if the
// chunk still fails after the reload, the error goes to the route error
// boundary, which offers a Reload button (BoundaryFallback, Error.tsx).

// eslint-disable-next-line no-restricted-syntax -- the one sanctioned React.lazy
import { ComponentType, lazy, LazyExoticComponent } from "react";

const RELOAD_KEY = "aeronet.chunkReloadAt";
// At most one automatic reload per tab in this window; a reload flag older than
// this belongs to an earlier deploy. Long enough to cover a cold-start boot
// (WakeGate) before the first lazy page.
const RELOAD_TTL_MS = 10 * 60 * 1000;
const RETRY_DELAY_MS = 500;

// Set when this page has started its reload: a second lazy chunk failing in the
// same render waits for the reload too, rather than showing the boundary.
let reloading = false;

/** A webpack JS chunk (ChunkLoadError) or mini-css-extract CSS chunk that failed to load. */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: unknown; code?: unknown; message?: unknown } | null;
  if (!e || typeof e !== "object") return false;
  if (e.name === "ChunkLoadError" || e.code === "CSS_CHUNK_LOAD_FAILED") return true;
  return typeof e.message === "string" && /Loading (CSS )?chunk \S+ failed/i.test(e.message);
}

/** True when this tab already reloaded for a failed chunk in the last RELOAD_TTL_MS. */
function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return at > 0 && Date.now() - at < RELOAD_TTL_MS;
  } catch {
    return false;
  }
}

/** Record the reload. False when storage is unavailable: without the flag a reload could loop. */
function markReloaded(): boolean {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return sessionStorage.getItem(RELOAD_KEY) !== null;
  } catch {
    return false;
  }
}

function clearReloaded(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* storage disabled: nothing was recorded */
  }
}

/** The boundary's Reload button: the user asked, so the loop guard starts afresh. */
export function reloadForNewVersion(): void {
  clearReloaded();
  window.location.reload();
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The import, retried once on a chunk failure; then one guarded page reload.
 * Any other error (the module threw while evaluating) is passed straight on.
 */
export async function importWithRetry<T>(
  factory: () => Promise<T>,
  retryDelayMs: number = RETRY_DELAY_MS
): Promise<T> {
  let error: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await wait(retryDelayMs);
    try {
      // A success does not clear the reload flag: a page whose layout chunk
      // loads and whose inner chunk is gone would otherwise reload forever.
      return await factory();
    } catch (e) {
      if (!isChunkLoadError(e)) throw e;
      error = e;
    }
  }
  if (reloading || (!reloadedRecently() && markReloaded())) {
    if (!reloading) window.location.reload();
    reloading = true;
    // Keep Suspense on its loading view until the browser leaves.
    return new Promise<T>(() => {});
  }
  throw error;
}

/** React.lazy, with a chunk that fails to load after a deploy recovered (see above). */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => importWithRetry(factory));
}

/** Test-only: the reload flag survives between tests otherwise. */
export function __resetChunkReloadForTests(): void {
  reloading = false;
  clearReloaded();
}
